import { useEffect, useRef, useState } from 'react';
import { useGame } from '../state/SocketProvider';
import { rememberSettings } from '../state/settingsPrefsStore';
import { MapView } from '../map/MapView';
import { BoundaryDraw } from './BoundaryDraw';
import { MapEditor } from './MapEditor';
import { GameSettings } from './GameSettings';
import { CardSettings } from './CardSettings';
import { ImportPack } from './ImportPack';
import { formatAreaStr } from '../util/format';
import { readinessReason } from '../../shared/readiness'; // v22 BOLT-115
import { HomeTurf, useAutoBoundary } from './HomeTurf'; // v23 BOLT-123/124/125/126
import {
  DEFAULT_TRAVEL_MODE,
  barrierPresetFor,
  type BarrierKind,
  type BoundaryOrigin,
  type TravelMode,
} from '../../shared/travelMode';
import { TEAM_LABEL } from '../../shared/teams'; // v22 BOLT-105/106
import { simStore, useSim } from '../state/sim';
import { useGeoWatch } from '../net/geo'; // v23 BOLT-124
import type { Map as MlMap } from 'maplibre-gl';
import type { LngLat, MapRegion } from '../../shared/protocol';
import { helpStore } from '../state/help';
import { setupStore, useFirstHiderChoice } from '../state/setup';
import { subscribePush, pushSupported, pushPermission } from '../net/push';
import { JoinQr } from '../ui/JoinQr';
import { LocationHelp } from '../ui/LocationHelp'; // v22 BOLT-110
import { PlayerStatusCells } from '../ui/PlayerStatus'; // v22 BOLT-116, v28 BOLT-153
import { FirstHiderSlider } from '../ui/FirstHiderSlider'; // v28 BOLT-138
import { dedupeSlots, lowBatteryWarning } from '../../shared/playerStatus'; // v22 BOLT-116

export function Lobby() {
  const { state, send, echoes, leave, status, lastError, subscribe } = useGame();
  const sim = useSim();
  const [copied, setCopied] = useState(false);
  const [msg, setMsg] = useState('');
  const [drawing, setDrawing] = useState(false);
  // v27 BOLT-146: the editor, and the regions it works on. PULLED when it opens
  // rather than carried on every state frame - the list runs to hundreds of
  // rings and `PublicState` goes out on every position update.
  const [editing, setEditing] = useState(false);
  const [regions, setRegions] = useState<MapRegion[]>([]);
  const [regionNotice, setRegionNotice] = useState<string | null>(null);
  // v23 BOLT-123/124: mode + barrier set + whether the boundary is still ours
  // to regenerate. AUTO regenerates on a mode or checklist change; CUSTOM (a
  // hand-drawn area, or a reused recent map) is never stomped.
  const [travelMode, setTravelMode] = useState<TravelMode>(DEFAULT_TRAVEL_MODE);
  const [boundaryOrigin, setBoundaryOrigin] = useState<BoundaryOrigin>('auto');
  const firstHider = useFirstHiderChoice();

  // v11 §2H: teams AUTO-ASSIGN — creator = Team A, first joiner = Team B.
  // The client sends the same pick_team message the picker used to, so the
  // wire (and the bots' legacy role path) is untouched.
  // v12.2: re-arm on reconnect — a send dropped around a socket death must
  // not latch the once-ref (the FirstHider hang's sibling).
  const autoPicked = useRef(false);
  const phase = state?.phase;
  const myTeam = state?.you.team ?? null;
  const isHost = state?.you.isHost ?? false;
  const slotFree = isHost ? !state?.players.teamA : !state?.players.teamB;
  // v19 BOLT-086: Practice is role-based (host=seeker, bot=hider), NOT teams —
  // so it must skip the team auto-assign, which would flip it into series mode.
  const practice = state?.practice ?? false;
  useEffect(() => {
    if (status !== 'open') {
      autoPicked.current = false;
      return;
    }
    if (phase !== 'lobby' || myTeam || !slotFree || autoPicked.current || practice) return;
    autoPicked.current = true;
    send({ t: 'pick_team', team: isHost ? 'A' : 'B' });
  }, [phase, myTeam, isHost, slotFree, status, practice, send]);

  // v19 BOLT-086: Practice auto-starts once the human (seeker) and the bot (hider)
  // are both present and GPS-ready — Landing → round, no lobby taps.
  const practiceStarted = useRef(false);
  const practiceReady =
    practice &&
    phase === 'lobby' &&
    isHost &&
    !!state?.boundary &&
    !!state?.players.hider &&
    !!state?.players.seeker;
  // v28 BOLT-152: NO readiness requirement. Practice moves by tapping the map,
  // so a GPS fix is not needed to play it - and requiring one stranded players
  // who opened it indoors. The server agrees (`locationGate` exempts practice),
  // so the two cannot disagree about whether a round may start.
  useEffect(() => {
    if (practiceReady && !practiceStarted.current) {
      practiceStarted.current = true;
      send({ t: 'start_headstart' });
    }
  }, [practiceReady, send]);

  // v23 BOLT-124 — the auto-boundary centres on the HOST'S OWN fix, straight
  // from the browser: in a teams lobby nobody has a role yet, so the server
  // ignores positions and `selfPin` is still null. The sim pin stands in when
  // the simulator is driving (keyless dev + E2E parity).
  const hostSetup = isHost && phase === 'lobby' && !practice;
  const geo = useGeoWatch(hostSetup && !sim.enabled);
  const turfCenter: LngLat | null = sim.enabled
    ? sim.pos
    : geo.sample
      ? { lat: geo.sample.lat, lng: geo.sample.lng }
      : (state?.selfPin ?? null);
  // BOLT-124 #7: on lobby load an area appears with NO user action, and flows
  // through the EXISTING boundary path unchanged, so the server sees an
  // ordinary boundary and needs no new logic.
  const turf = useAutoBoundary({
    center: turfCenter,
    mode: travelMode,
    origin: boundaryOrigin,
    enabled: hostSetup,
    // v25 BOLT-140: the play area is sized from the headstart the room is
    // actually set to, so changing the headstart resizes the map.
    headStartMs: state?.settings?.headStartMs,
    // v26 BOLT-145: ASK, do not compute. The server harvests its own barriers
    // from pinned tiles and answers with the boundary on the ordinary state
    // broadcast, so there is still exactly one boundary path.
    ask: (req) => send({ t: 'auto_boundary', mode: req.mode, center: req.center, lines: req.lines ?? [] }),
    lastError,
    boundaryToken: state?.boundary,
  });

  // v27 BOLT-146: regions are PULLED, not broadcast (hundreds of rings, and
  // `PublicState` goes out on every position frame), so the editor listens for
  // the reply here.
  useEffect(() => {
    return subscribe((m) => {
      if (m.t === 'map_regions') {
        setRegions(m.regions);
        setRegionNotice(null);
      } else if (m.t === 'error' && m.code === 'would_split') {
        setRegionNotice('That area is holding the map together. Remove an outer one first.');
      }
    });
  }, [subscribe]);

  if (!state) return null;

  const { code, you, players, boundary } = state;
  const deepLink = `${location.origin}/?code=${code}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(deepLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };
  if (editing) {
    return (
      <MapEditor
        regions={regions}
        center={turfCenter}
        boundary={boundary ?? null}
        notice={regionNotice}
        onPick={(ids) => {
          // The host CHOSE this, so a later auto pass must not stomp it - the
          // same rule a hand-drawn map has had since v23.
          setBoundaryOrigin('custom');
          send({ t: 'set_face_selection', ids });
        }}
        onDrawCustom={() => {
          setEditing(false);
          setDrawing(true);
        }}
        onDone={() => setEditing(false)}
      />
    );
  }

  if (drawing) {
    return (
      <BoundaryDraw
        // sim users' "current location" is their tapped sim pin (E2E parity)
        center={state.selfPin ?? (sim.enabled ? sim.pos : null)}
        onCancel={() => setDrawing(false)}
        onConfirm={(b) => {
          // v23: a HAND-DRAWN area is the player's. Marking it CUSTOM stops the
          // auto-boundary from regenerating over it, which is the whole
          // "nothing gets stomped" rule. (Missing this silently overwrote a
          // freshly drawn boundary a beat later.)
          setBoundaryOrigin('custom');
          send({ t: 'set_boundary', boundary: b });
          setDrawing(false);
        }}
      />
    );
  }

  const seriesPicked = !!players.teamA || !!players.teamB;
  const bothTeamed = (!!players.hider && !!players.seeker) || (!!players.teamA && !!players.teamB);

  // v19 BOLT-088: the readiness start-gate — every teamed player must be on
  // precise GPS before the host can start. Booleans ride the roster slots.
  // v24 (Jake): DEDUPE. In a series the same person is both a role and a team,
  // so this listed each player twice - visibly in the status rows, and silently
  // in the readiness count and the low-battery warning.
  const teamedSlots = dedupeSlots([players.hider, players.seeker, players.teamA, players.teamB]);
  const unready = teamedSlots.filter((p) => !(p.locationPrecise && p.hasFix));
  const allReady = bothTeamed && unready.length === 0;
  const mySlot = you.team
    ? you.team === 'A'
      ? players.teamA
      : players.teamB
    : you.role === 'hider'
      ? players.hider
      : players.seeker;
  const iNeedGps = !!mySlot && !(mySlot.locationPrecise && mySlot.hasFix);

  // v22 BOLT-116: never blocks start; it only tells the host what they are
  // walking into.
  const lowBattery = lowBatteryWarning(teamedSlots);

  // v29 BOLT-159 — READY, and who starts the round.
  //
  // Jake: "both players should be required to press ready before the game can
  // start. Once all players are ready, the hider (not the host) should be the
  // one to see the begin head start button."
  //
  // READY ABSORBS THE GPS GATE (his call): the control is disabled until this
  // device is actually ready and says why, so there is ONE thing to look at
  // rather than two gates in a row. The server enforces the same rule.
  // Capability-gated: a room holding an older build runs the v28 flow, because
  // that client can never press Ready and a gate nobody can open is a locked
  // door. The server decides; the lobby just renders what it is told.
  const readyFlow = state.readyGateActive === true;
  const iAmReady = !!mySlot?.ready;
  const canReady = !!mySlot && !iNeedGps && mySlot.connected;
  const everyoneReady = teamedSlots.length > 0 && teamedSlots.every((p) => p.ready);
  // The round's hider starts it. `series.firstHider` is resolved the moment the
  // room goes unanimous, so before that nobody is offered the button.
  const hiderTeam = everyoneReady ? state.series.firstHider : null;
  const iStart = !!hiderTeam && you.team === hiderTeam;

  // v27 BOLT-150 (Jake): "the small map container is hard to see since it's
  // small. I think when the user clicks into it, it should go full screen." So
  // the preview is a way IN, and it re-frames on every area change so the host
  // sees the shape rather than a corner of it. Sim keeps the tap when it is on:
  // it is a developer toggle and setting a position from the lobby map is how
  // local play works.
  //
  // v28 BOLT-155: defined ONCE here and rendered INSIDE the play-area container
  // on both paths - the host's `HomeTurf`, and the joiner's status panel. All
  // three v27 behaviours ride along unchanged: `refitBoundary`, the host-only
  // tap into the editor, and the build dim.
  const preview = (
    <div
      className={`section-gap map-preview${turf.building ? ' map-preview--busy' : ''}`}
      data-testid="map-preview"
      data-busy={turf.building ? 'true' : 'false'}
    >
      <MapView
        state={state}
        interactive
        refitBoundary
        onMapClick={
          sim.enabled
            ? (p) => simStore.setPos(p)
            : you.isHost
              ? () => {
                  setRegionNotice(null);
                  send({ t: 'get_regions' });
                  setEditing(true);
                }
              : undefined
        }
      />
    </div>
  );

  return (
    <div className="screen">
      <div className="screen__inner">
        <div className="row between">
          <div className="eyebrow lobby-eyebrow">
            Game setup · <span data-testid="lobby-code">{code}</span>
          </div>
          <div className="row" style={{ gap: '0.4rem' }}>
            <span className={`dot ${you.isHost ? 'dot--signal' : 'dot--on'}`} />
            <span className="eyebrow">{you.isHost ? 'Host' : 'Joined'}</span>
          </div>
        </div>

        <div className="lobby-title">
          Get ready to <em>bolt!</em>
        </div>

        <button
          className="btn btn--ghost"
          data-testid="howto-open"
          style={{ width: 'auto', alignSelf: 'flex-start', padding: '0.4rem 0.85rem' }}
          onClick={() => helpStore.open()}
        >
          How to play
        </button>

        {/* v28 BOLT-153 — the lobby drives to ONE next step, so every fact is
            said in exactly one place.
              · the Players container is GONE; status lives inside the team
                container whose identity it belongs to
              · the "Waiting for a second player…" banner is GONE; the join
                affordance is ON the empty slot, which IS the thing that needs
                action
              · `schedule-summary` is GONE; it restated Game settings, one tap
                away.
            While a slot is empty the roster stacks full width, so the thing
            needing action gets the room to carry a code and a QR. */}
        <div className="section-gap teams" data-testid="team-roster" data-waiting={!bothTeamed}>
          {(['A', 'B'] as const).map((team) => {
            const slot = team === 'A' ? players.teamA : players.teamB;
            const mine = you.team === team;
            return (
              // v22 BOLT-105: a solid colour swatch, so which team you are on
              // reads at a glance across a street. Orange = signal fill with
              // bone text; White = bone fill with slate text. Both AA.
              // v28: an EMPTY slot drops the fill. Nobody's identity is there
              // yet, and a code, a QR and a button need a dark surface to be
              // legible on.
              <div
                key={team}
                className={`team-tile team-tile--${team.toLowerCase()}`}
                data-active={mine}
                data-filled={!!slot}
                data-testid={`roster-${team.toLowerCase()}`}
              >
                {/* v22 BOLT-107: the "then rotates" subtext is gone. It said
                    the same thing as the "Who hides first" chooser directly
                    below it. */}
                <div className="team-tile__role">{TEAM_LABEL[team]}</div>
                <div className="team-tile__who" data-testid={`roster-${team.toLowerCase()}-who`}>
                  {/* v22 BOLT-106: no name entry anywhere. The colour IS the
                      identity, so there is nothing to type, and no free text
                      that could turn out to be somebody's real name. */}
                  {slot ? (mine ? 'You' : 'Joined') : '· waiting to join ·'}
                </div>
                {slot ? (
                  // v28 BOLT-153: the readouts, with no name attached. The tile
                  // already says whose they are, which is the whole point.
                  <div className="team-tile__status" data-testid={`roster-${team.toLowerCase()}-status`}>
                    <PlayerStatusCells slot={slot} />
                  </div>
                ) : bothTeamed ? null : ( // PRACTICE fills the ROLE slots, not the team ones, so
                  // an empty team tile there is not an invitation - it is a
                  // room that is already full. The v22 share panel keyed on
                  // `bothTeamed` for exactly this reason; keep the rule.
                  // Jake: "Something like 'White Team - use code XXXXXX or scan
                  // code to join'". The instruction belongs on the slot it is
                  // about, not in a banner above the whole screen.
                  <div className="team-tile__join" data-testid={`join-${team.toLowerCase()}`}>
                    <div className="team-tile__join-line">
                      Use code{' '}
                      <span className="team-tile__join-code" data-testid="room-code">
                        {code}
                      </span>
                      or scan to join.
                      <button
                        className="btn"
                        style={{ width: 'auto', padding: '0.45rem 0.8rem', marginTop: '0.5rem' }}
                        data-testid="copy-link"
                        onClick={copy}
                      >
                        {copied ? 'Copied' : 'Copy link'}
                      </button>
                    </div>
                    {/* v11 §2I: scan with the iPhone Camera — opens the app (or the web join) */}
                    <JoinQr url={deepLink} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* v11 §2H: the host's ONE setup screen — rounds/win live in
            GameSettings; the first-hider pick is pre-selected here and
            auto-sent when the phase opens (no standalone screen).
            v28 BOLT-138: a three-detent slider. The ends ARE the teams and the
            middle is the coin flip, so the geometry says what three labelled
            buttons had to spell out. */}
        {you.isHost && (
          <div className="panel section-gap" data-testid="first-hider-panel">
            <div className="field__label">Who hides first</div>
            <FirstHiderSlider
              value={firstHider}
              onChange={(c) => {
                setupStore.setFirstHider(c);
                // v29 BOLT-159: the server needs the pick DURING the lobby now.
                // It is what decides who is handed "Begin head start", and with
                // 'random' the roll cannot happen until the room is unanimous.
                send({ t: 'choose_first_hider', team: c });
              }}
              youAre={you.team}
            />
          </div>
        )}

        {you.isHost && seriesPicked && <GameSettings />}
        {you.isHost && seriesPicked && <CardSettings />}
        {you.isHost && <ImportPack />}

        {/* Host: draw / lock the play area */}
        {you.isHost ? (
          <div data-testid="boundary-panel">
            {/* v23 — Home Turf: the map is already there when you open the
                lobby. The primary view is three mode icons and Start.
                v27 BOLT-150: and ONE map button, which opens the editor.
                v28 BOLT-155: and the PREVIEW is inside it now, between the mode
                picker and that button. It was rendered here, outside the
                container whose heading named it, which is exactly what Jake
                called strange. */}
            <HomeTurf
              preview={preview}
              center={turfCenter}
              boundary={boundary ?? null}
              origin={boundaryOrigin}
              mode={travelMode}
              building={turf.building}
              failed={turf.failed}
              shortCoverage={turf.shortCoverage}
              // v25 BOLT-144: the reason and the composition both come from the
              // build that produced the polygon on screen, so neither can drift
              // away from what the player is looking at.
              reason={turf.reason}
              composition={turf.composition}
              safetyNotices={state.safetyNotices}
              areaSqMeters={state.zoneAreaSqMeters}
              onWiden={() => turf.rebuild()}
              onModeChange={(m) => {
                setTravelMode(m);
                // v29 BOLT-142: remembered for the next game they create.
                rememberSettings({ travelMode: m });
                send({ t: 'set_settings', settings: { travelMode: m } });
              }}
              canEdit={you.isHost}
              onEdit={() => {
                setRegionNotice(null);
                send({ t: 'get_regions' });
                setEditing(true);
              }}
              onRegenerate={() => {
                setBoundaryOrigin('auto');
                turf.rebuild();
              }}
            />
          </div>
        ) : (
          // A joiner has no controls, but they still get the map - and it is in
          // a container labelled by what it is, for the same reason.
          <div className="panel section-gap" data-testid="boundary-panel">
            <div className="row" style={{ gap: '0.6rem' }}>
              <span className={`dot ${boundary ? 'dot--on' : 'dot--warn'}`} />
              <span className="soft" style={{ fontSize: '0.85rem' }} data-testid="boundary-status">
                {boundary ? 'Play area is set.' : 'Host is setting the play area…'}
              </span>
            </div>
            {preview}
          </div>
        )}

        {/* Comms check — confirms the round-trip through the server. */}
        <div className="panel section-gap">
          <div className="field__label">Radio check</div>
          <div className="row" style={{ gap: '0.5rem' }}>
            <input
              className="input"
              placeholder="say something…"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              data-testid="echo-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && msg.trim()) {
                  send({ t: 'echo', text: msg.trim() });
                  setMsg('');
                }
              }}
            />
            <button
              className="btn"
              style={{ width: 'auto', padding: '0.6rem 1rem' }}
              data-testid="echo-send"
              onClick={() => {
                if (msg.trim()) {
                  send({ t: 'echo', text: msg.trim() });
                  setMsg('');
                }
              }}
            >
              Send
            </button>
          </div>
          <div className="stack" style={{ gap: 4, marginTop: 8 }} data-testid="echo-log">
            {echoes.slice(-4).map((e, i) => (
              <div key={i} className="mono" style={{ fontSize: '0.72rem', color: 'var(--bone-soft)' }}>
                <span style={{ color: 'var(--signal)' }}>{e.from}</span> · {e.text}
              </div>
            ))}
          </div>
        </div>

        {pushSupported() && pushPermission() !== 'granted' && state.vapidPublicKey && (
          <button
            className="btn section-gap"
            data-testid="enable-alerts"
            onClick={() => subscribePush(state.vapidPublicKey, (sub) => send({ t: 'push_subscribe', platform: 'web', subscription: sub }), { prompt: true })}
          >
            Enable alerts (recommended on a phone)
          </button>
        )}

        {/* v12 §2B: the escape hatch — an OBVIOUS way out of a joined lobby;
            the host's exit closes the room for everyone (session_closed). */}
        <button
          className="btn btn--ghost section-gap"
          data-testid="leave-lobby"
          onClick={() => {
            send({ t: 'leave' });
            leave();
          }}
        >
          {you.isHost ? 'Close this lobby' : 'Leave this lobby'}
        </button>

        <div style={{ flex: 1 }} />
        {/* v28 BOLT-153: the "Players" container is GONE. Every readout it held
            now lives inside the Orange and White team containers above, where
            the identity already is. Nothing is said twice. */}

        {/* v22 BOLT-116: a CAUTION, never a wall. A dying phone is the players'
            call; the round can still start. Only players who actually report
            battery (iOS) can raise it. */}
        {you.isHost && lowBattery && (
          <div className="banner banner--signal" data-testid="low-battery-warning">
            <span className="banner__detail">{lowBattery}</span>
          </div>
        )}

        {/* v22 BOLT-110: the warning carries an action and a "why?" now, and
            BOLT-115's live reason tells them the actual number. */}
        {iNeedGps && <LocationHelp reason={mySlot ? readinessReason(mySlot) : null} />}
        {/* v29 BOLT-159: READY is every player's control, and it is the only
            one that mentions GPS. It cannot be pressed on a device that is not
            ready, and `LocationHelp` above already says why. */}
        {readyFlow && mySlot && (
          <button
            className={`btn section-gap${iAmReady ? '' : ' btn--signal'}`}
            data-testid="ready-toggle"
            data-ready={iAmReady ? 'true' : 'false'}
            disabled={!canReady && !iAmReady}
            onClick={() => send({ t: 'set_ready', ready: !iAmReady })}
          >
            {iAmReady ? "I'm ready. Tap to undo" : "I'm ready"}
          </button>
        )}

        {bothTeamed && boundary && allReady && (readyFlow ? everyoneReady && iStart : you.isHost) ? (
          <button
            className="btn btn--signal btn--lg"
            style={{ marginTop: '1rem' }}
            data-testid="begin-headstart"
            onClick={() => send({ t: 'start_headstart' })}
          >
            Begin head start
          </button>
        ) : (
          <div className="eyebrow" style={{ textAlign: 'center', marginTop: '1rem' }} data-testid="lobby-footer">
            {!bothTeamed
              ? 'Waiting for both teams…'
              : !boundary
                ? you.isHost
                  ? 'Draw the play area to continue'
                  : 'Waiting on the host'
                : !allReady
                  ? // v22 BOLT-115: say WHY, with the number, so the player
                    // knows whether to wait or step outside.
                    `Waiting on precise GPS: ${unready
                      .map((u) => {
                        const why = readinessReason(u);
                        return why ? `${u.name} (${why})` : u.name;
                      })
                      .join(', ')}`
                  : // v29 BOLT-159: name who has not readied, then name who we
                    // are waiting on to press start. "Waiting on the host" was
                    // wrong the moment the hider became the one who starts.
                    readyFlow && !everyoneReady
                    ? `Waiting on ${teamedSlots
                        .filter((p) => !p.ready)
                        .map((p) => p.name)
                        .join(', ')} to be ready`
                    : readyFlow
                      ? `Waiting on ${hiderTeam ? TEAM_LABEL[hiderTeam] : 'the hider'} to start`
                      : 'Waiting on the host to start'}
          </div>
        )}
      </div>
    </div>
  );
}
